import React, { useContext, useEffect, useMemo, useState } from "react"
import { Linking, Platform, ScrollView, Text, View } from "react-native"
import { StackActions, useNavigation } from "@react-navigation/native"
import { MaterialCommunityIcons } from "@expo/vector-icons"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { ThemeContext } from "../utility/context/ThemeContext"
import { textStyles } from "../style"
import { MAX_CONTENT_WIDTH, extra } from "../config"
import { ThemedButton } from "../utility/input/buttons"
import { ThemedTextInput } from "../utility/input/textinputs/themed-text-input"

type Props = {
  route: {
    params: {
      state?: string
      token?: string
      theme?: string
      firstName?: string
      lastName?: string
    }
  }
}

const CollectName = ({ route }: Props): React.ReactElement<Props> => {
  const { theme, setTheme } = useContext(ThemeContext)
  const { textMain, bgMain, danger } = theme.colors
  const navigation = useNavigation()
  const { top, bottom } = useSafeAreaInsets()
  const stateParam = route.params?.state
  const tokenParam = route.params?.token
  const themeParam = route.params?.theme

  const [firstName, setFirstName] = useState(route.params?.firstName || "")
  const [lastName, setLastName] = useState(route.params?.lastName || "")
  const [error, setError] = useState<string | undefined>(undefined)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (themeParam) {
      setTheme(themeParam)
    }

    if (!stateParam || !tokenParam) {
      navigation.dispatch(StackActions.replace("Login"))
    }
  }, [navigation, setTheme, stateParam, themeParam, tokenParam])

  const isSubmitDisabled = useMemo(() => {
    return !firstName.trim() || !lastName.trim() || submitting
  }, [firstName, lastName, submitting])

  // AUTO-SCROLL INPUT INTO VIEW ON MOBILE WEB
  useEffect(() => {
    if (Platform.OS !== "web") {
      return
    }
    
    const handler = (e: Event) => {
      const target = e.target as { scrollIntoView?: (opts?: ScrollIntoViewOptions) => void } | null
      if (!target?.scrollIntoView) return

      setTimeout(() => {
        target.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }, 250)
    }

    document.addEventListener("focusin", handler)
    return () => document.removeEventListener("focusin", handler)
  }, [])

  const handleContinue = async () => {
    if (submitting) {
      return
    }

    const trimmedFirst = firstName.trim()
    const trimmedLast = lastName.trim()

    if (!trimmedFirst || !trimmedLast) {
      setError("Please enter both your first and last name.")
      return
    }

    if (!route.params?.state || !route.params?.token) {
      setError("Your login session expired. Please sign in again.")
      return
    }

    try {
      setSubmitting(true)
      setError(undefined)

      const params = new URLSearchParams()
      params.set("state", route.params.state)
      params.set("token", route.params.token)
      params.set("firstName", trimmedFirst)
      params.set("lastName", trimmedLast)
      params.set("status", "success")

      const continueUrl = `https://${extra.REACT_APP_AUTH0_DOMAIN}/continue?${params.toString()}`
      await Linking.openURL(continueUrl)
    } catch (e: any) {
      setError(e?.message || "Unable to continue login. Please try again.")
      setSubmitting(false)
    }
  }

  return (
    <ScrollView
      style={{
        flex: 1,
        backgroundColor: bgMain,
      }}
      contentContainerStyle={{
        flexGrow: 1,
        justifyContent: "center",
        alignItems: "center",
        paddingTop: top || 16,
        paddingBottom: bottom || 16,
        paddingHorizontal: 16,
      }}
      keyboardShouldPersistTaps="handled"
    >
      <View style={{ width: "100%", maxWidth: MAX_CONTENT_WIDTH, gap: 14 }}>
        <Text style={[textStyles.h3, { color: textMain, textAlign: "center" }]}>
          Welcome! Let&apos;s get you set up.
        </Text>

          <View style={{ width: "100%", alignItems: "center" }}>
            <MaterialCommunityIcons name="account-edit-outline" color={textMain} size={130} />
          </View>

          <Text style={[textStyles.body, { color: textMain, textAlign: "center" }]}>
            We will need your first and last name to move forward in creating your account.
          </Text>

        <ThemedTextInput
          label="First Name"
          placeholder="Jane"
          value={firstName}
          onChange={(value) => {
            setFirstName(value)
            if (error) setError(undefined)
          }}
          textInputProps={{
            autoCapitalize: "words",
            autoCorrect: false,
            textContentType: "givenName",
            returnKeyType: "next",
          }}
        />

        <ThemedTextInput
          label="Last Name"
          placeholder="Doe"
          value={lastName}
          onChange={(value) => {
            setLastName(value)
            if (error) setError(undefined)
          }}
          textInputProps={{
            autoCapitalize: "words",
            autoCorrect: false,
            textContentType: "familyName",
            returnKeyType: "done",
            onSubmitEditing: handleContinue,
          }}
        />

        {error ? (
          <Text style={[textStyles.body, { color: danger, textAlign: "center" }]}>
            {error}
          </Text>
        ) : null}

        <View style={{ width: "100%", paddingTop: 8 }}>
          <ThemedButton
            text="Continue"
            onPress={handleContinue}
            loading={submitting}
            disabled={isSubmitDisabled}
            variant="primary"
          />
        </View>
      </View>
    </ScrollView>
  )
}

export default CollectName
